import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { ModalTypes } from "@lib/enums/ModalTypes";
import type { UserIdentity } from "@lib/auth/types";
import type { ClubSignupMethod } from "@lib/analytics/analyticsTypes";

export interface SignupFormData extends UserIdentity {
    dateOfBirth: string;
    gender: string;
    phone: string;
    countryCode: string;
    newsletter: boolean;
}

interface AuthFormState {
    signup: Partial<SignupFormData>;
    login: { email: string };
    forgotPassword: { email: string };
    postResetEmail: string | null;
    returnModalType: ModalTypes | null;
    // Selected social provider, persisted across the OAuth redirect so the
    // "signup_method_selected" analytics event can fire once the user is back on-site.
    pendingSignupMethod: ClubSignupMethod | null;

    setSignupField: <K extends keyof SignupFormData>(field: K, value: SignupFormData[K]) => void;
    setSignupFields: (fields: Partial<SignupFormData>) => void;
    setLoginEmail: (email: string) => void;
    setForgotPasswordEmail: (email: string) => void;
    setPostResetEmail: (email: string | null) => void;
    setReturnModal: (modalType: ModalTypes | null) => void;
    setPendingSignupMethod: (method: ClubSignupMethod | null) => void;
    clearSignup: () => void;
    clearLogin: () => void;
    clearAll: () => void;
}

const initialSignup: Partial<SignupFormData> = {};
const initialLogin = { email: "" };
const initialForgotPassword = { email: "" };

export const useAuthFormStore = create<AuthFormState>()(
    persist(
        (set) => ({
            signup: initialSignup,
            login: initialLogin,
            forgotPassword: initialForgotPassword,
            postResetEmail: null,
            returnModalType: null,
            pendingSignupMethod: null,

            setSignupField: (field, value) =>
                set((state) => ({
                    signup: { ...state.signup, [field]: value },
                })),

            setSignupFields: (fields) =>
                set((state) => ({
                    signup: { ...state.signup, ...fields },
                })),

            setLoginEmail: (email) => set({ login: { email } }),

            setForgotPasswordEmail: (email) => set({ forgotPassword: { email } }),

            setPostResetEmail: (email) => set({ postResetEmail: email }),

            setReturnModal: (modalType) => set({ returnModalType: modalType }),

            setPendingSignupMethod: (method) => set({ pendingSignupMethod: method }),

            clearSignup: () => set({ signup: initialSignup }),

            clearLogin: () => set({ login: initialLogin }),

            clearAll: () =>
                set({
                    signup: initialSignup,
                    login: initialLogin,
                    forgotPassword: initialForgotPassword,
                    postResetEmail: null,
                    returnModalType: null,
                    pendingSignupMethod: null,
                }),
        }),
        {
            name: "ecco-auth-form",
            storage: createJSONStorage(() => sessionStorage),
        }
    )
);
